import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { formatDate } from "@/lib/utils";
import { AdminRangeCalendar } from "@/components/forms";
import { useLazyTableParams } from "@/lib/useLazyTableParams";
import { useListPageParam } from "@/lib/useListPageParam";
import { useListSearchParam } from "@/lib/useListSearchParam";
import {
  joinAnd,
  likeFilter,
  useAdminEventsList,
  type AdminEventListItem,
} from "@/lib/admin-api";

function parseDateRangeFromParams(
  fromParam: string | null,
  toParam: string | null,
): Date[] | null {
  if (!fromParam || !toParam) return null;
  const from = new Date(fromParam);
  const to = new Date(toParam);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  return [from, to];
}

export function EventsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [globalFilter, setGlobalFilter] = useListSearchParam("q");

  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");
  const dateRangeApplied = useMemo(
    () => parseDateRangeFromParams(fromParam, toParam),
    [fromParam, toParam],
  );
  const hidePastEvents = searchParams.get("hidePast") !== "false";

  // PrimeReact Calendar range mode can throw if it receives an array containing `null`.
  // Represent range as: null | [start] | [start, end]. Initialise from URL so it survives navigation.
  const [dateRangeUi, setDateRangeUi] = useState<Date[] | null>(
    () => dateRangeApplied ?? null,
  );

  const EVENTS_PAGE_SIZE = 10;
  const [pageFromUrl, setPageFromUrl] = useListPageParam();

  const {
    lazyParams,
    setLazyParams,
    onPage: onPageBase,
    onSort,
    resetToFirstPage: resetToFirstPageBase,
  } = useLazyTableParams({
    initialRows: EVENTS_PAGE_SIZE,
    initialSortField: "startsAt",
    initialSortOrder: 1,
    initialFirst: (Math.max(1, pageFromUrl) - 1) * EVENTS_PAGE_SIZE,
  });

  const onPage = useCallback(
    (event: { first: number; rows: number; page?: number }) => {
      onPageBase(event);
      const newPage = (event.page ?? Math.floor(event.first / event.rows)) + 1;
      setPageFromUrl(newPage);
    },
    [onPageBase, setPageFromUrl],
  );

  const resetToFirstPage = useCallback(() => {
    resetToFirstPageBase();
    setPageFromUrl(1);
  }, [resetToFirstPageBase, setPageFromUrl]);

  useEffect(() => {
    setLazyParams((prev) => {
      const expectedFirst = (Math.max(1, pageFromUrl) - 1) * prev.rows;
      if (prev.first === expectedFirst) return prev;
      return {
        ...prev,
        first: expectedFirst,
        page: Math.max(0, pageFromUrl - 1),
      };
    });
  }, [pageFromUrl, setLazyParams]);

  const filter = useMemo(() => {
    const hasCompleteDateRange =
      dateRangeApplied != null && dateRangeApplied.length === 2;

    let dateFrom: string | undefined;
    let dateTo: string | undefined;
    if (hasCompleteDateRange) {
      const [from, to] = dateRangeApplied;
      dateFrom = new Date(
        from.getFullYear(),
        from.getMonth(),
        from.getDate(),
        0,
        0,
        0,
        0,
      ).toISOString();
      dateTo = new Date(
        to.getFullYear(),
        to.getMonth(),
        to.getDate(),
        23,
        59,
        59,
        999,
      ).toISOString();
    }

    const filterParts: string[] = [];
    const search = globalFilter.trim();
    if (search !== "") {
      filterParts.push(likeFilter("title", search));
    }
    if (dateFrom) {
      filterParts.push(`startsAt>=${JSON.stringify(dateFrom)}`);
    }
    if (dateTo) {
      filterParts.push(`startsAt<=${JSON.stringify(dateTo)}`);
    }
    if (hidePastEvents && !hasCompleteDateRange) {
      filterParts.push(`endsAt>=${JSON.stringify(new Date().toISOString())}`);
    }

    return joinAnd(filterParts);
  }, [dateRangeApplied, globalFilter, hidePastEvents]);

  const eventsQuery = useAdminEventsList({
    page: lazyParams.page + 1,
    pageSize: lazyParams.rows,
    sortField: lazyParams.sortField,
    sortOrder: lazyParams.sortOrder,
    filter,
  });
  const events = eventsQuery.data?.data ?? [];
  const totalRecords = eventsQuery.data?.pagination.total ?? 0;
  const loading = eventsQuery.isFetching;

  const onFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilter(value);
    resetToFirstPage();
  };

  const onDateRangeChange = (value: unknown) => {
    let next: Date[] | null = null;
    if (value instanceof Date) {
      // PrimeReact can emit a single Date after the first click in range mode.
      next = [value];
    } else if (Array.isArray(value)) {
      const dates = value.filter((v): v is Date => v instanceof Date).slice(0, 2);
      next = dates.length > 0 ? dates : null;
    } else {
      next = null;
    }

    setDateRangeUi(next);
    resetToFirstPage();
    if (next === null || next.length === 2) {
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          if (next !== null && next.length === 2) {
            n.set("from", next[0].toISOString());
            n.set("to", next[1].toISOString());
          } else {
            n.delete("from");
            n.delete("to");
          }
          return n;
        },
        { replace: true },
      );
    }
  };

  const clearDateRange = () => {
    setDateRangeUi(null);
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete("from");
        n.delete("to");
        return n;
      },
      { replace: true },
    );
    resetToFirstPage();
  };

  const onHidePastEventsChange = (next: boolean) => {
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.set("hidePast", String(next));
        return n;
      },
      { replace: true },
    );
    resetToFirstPage();
  };

  const statusBodyTemplate = (rowData: AdminEventListItem) => {
    return (
      <Tag
        value={
          rowData.isCancelled
            ? "Cancelled"
            : rowData.isPublished
              ? "Published"
              : "Draft"
        }
        severity={
          rowData.isCancelled
            ? "danger"
            : rowData.isPublished
              ? "success"
              : "warning"
        }
      />
    );
  };

  const dateBodyTemplate = (rowData: AdminEventListItem) => {
    return rowData.startsAt ? formatDate(rowData.startsAt) : "-";
  };

  const actionBodyTemplate = (rowData: AdminEventListItem) => {
    const handleEdit = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      void navigate(`/events/${rowData.id}`);
    };

    const handleViewParticipants = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      void navigate(`/events/${rowData.id}/participants`);
    };

    const handleClone = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      void navigate(`/events/new?cloneFrom=${rowData.id}`);
    };

    return (
      <div className="flex gap-0 flex-nowrap" onClick={(e) => e.stopPropagation()}>
        <Button
          type="button"
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          onClick={handleEdit}
          aria-label="Edit"
        />
        <Button
          type="button"
          icon="pi pi-copy"
          className="p-button-text p-button-sm"
          onClick={handleClone}
          aria-label="Clone"
        />
        <Button
          type="button"
          icon="pi pi-users"
          className="p-button-text p-button-sm"
          onClick={handleViewParticipants}
          aria-label="View participants"
        />
      </div>
    );
  };

  return (
    <div className="card">
      <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-4 gap-4">
        <h1 className="text-2xl font-bold">Events</h1>
        <div className="flex gap-2 flex-column md:flex-row md:flex-wrap md:justify-content-end">
          <div className="flex align-items-center gap-2 w-full md:w-auto">
            <Checkbox
              inputId="hidePastEvents"
              checked={hidePastEvents}
              onChange={(e) => onHidePastEventsChange(Boolean(e.checked))}
              disabled={dateRangeUi != null}
            />
            <label htmlFor="hidePastEvents" className="text-sm text-gray-700">
              Hide past events
            </label>
          </div>
          <span className="w-full md:w-auto">
            <AdminRangeCalendar
              value={dateRangeUi}
              selectionMode="range"
              onChange={(e) => onDateRangeChange(e.value)}
              showIcon
              placeholder="Filter by date range"
              className="w-full"
            />
          </span>
          <Button
            type="button"
            label="Clear"
            icon="pi pi-filter-slash"
            onClick={clearDateRange}
            className="w-full md:w-auto p-button-outlined"
            disabled={dateRangeApplied == null && dateRangeUi == null}
          />
          <span className="p-input-icon-left w-full md:w-auto">
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={onFilter}
              placeholder="Search events..."
              className="w-full"
            />
          </span>
          <Button
            label="New Event"
            icon="pi pi-plus"
            onClick={() => {
              void navigate("/events/new");
            }}
            className="w-full md:w-auto p-button-outlined"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <DataTable
          value={events}
          lazy
          paginator
          rows={lazyParams.rows}
          first={lazyParams.first}
          totalRecords={totalRecords}
          onPage={onPage}
          onSort={onSort}
          sortField={lazyParams.sortField}
          sortOrder={lazyParams.sortOrder}
          loading={loading}
          responsiveLayout="scroll"
          emptyMessage="No events found"
          className="text-sm"
        >
          <Column
            field="title"
            header="Title"
            sortable
            style={{ minWidth: "200px" }}
          />
          <Column
            field="isPublished"
            header="Status"
            body={statusBodyTemplate}
            style={{ minWidth: "120px" }}
          />
          <Column
            field="startsAt"
            header="Start Date"
            body={dateBodyTemplate}
            sortable
            style={{ minWidth: "140px" }}
          />
          <Column
            body={actionBodyTemplate}
            header="Actions"
            style={{ minWidth: "100px" }}
          />
        </DataTable>
      </div>
    </div>
  );
}
